/**
 * The app's icon set: one consistent 24×24 stroke system, drawn in
 * `currentColor` so icons inherit the colour of whatever button holds them.
 *
 * These replace the emoji/Unicode glyphs the UI used to render inline (🏷 ☆ ⋯
 * ⚙ ▾ …), which picked up platform-specific sizes and — for the emoji ones —
 * platform-specific *colours*, so a single toolbar never read as one family.
 *
 * Geometry follows the Lucide icon set (ISC licensed, github.com/lucide-icons)
 * so the shapes stay on a shared grid and stroke weight; only the icons the app
 * actually uses are inlined here, which keeps this dependency-free.
 *
 * Icons are decorative by default (`aria-hidden`): the surrounding button
 * carries the accessible name. Pass `title` only for a standalone icon that is
 * itself the whole meaning.
 */

/** Icons drawn as strokes (the default: `fill: none`, `stroke: currentColor`). */
const STROKE_PATHS = {
  // — Navigation & chrome —
  x: 'M18 6 6 18M6 6l12 12',
  'chevron-down': 'm6 9 6 6 6-6',
  'chevron-left': 'm15 18-6-6 6-6',
  'chevron-right': 'm9 18 6-6-6-6',
  'arrow-left': 'm12 19-7-7 7-7M19 12H5',
  plus: 'M5 12h14M12 5v14',
  minus: 'M5 12h14',
  search: 'M21 21l-4.3-4.3',

  // — Library —
  pencil:
    'M21.2 6.8a1 1 0 0 0-4-4L3.8 16.2a2 2 0 0 0-.5.8l-1.3 4.4a.5.5 0 0 0 .6.6l4.4-1.3a2 2 0 0 0 .8-.5zM15 5l4 4',
  trash:
    'M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6M10 11v6M14 11v6',
  copy: 'M4 16a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2',
  folder:
    'M20 20a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2Z',
  'folder-input':
    'M2 9V5a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.69.9l.81 1.2a2 2 0 0 0 1.67.9H20a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2v-3M2 13h10M9 16l3-3-3-3',
  download: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3',
  upload: 'M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12',
  link: 'M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71',
  lock: 'M7 11V7a5 5 0 0 1 10 0v4',
  share: 'M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8M16 6l-4-4-4 4M12 2v13',
  outline: 'M4 6h16M8 12h12M8 18h12',
  settings:
    'M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z',
  command: 'M15 6v12a3 3 0 1 0 3-3H6a3 3 0 1 0 3 3V6a3 3 0 1 0-3 3h12a3 3 0 1 0-3-3',

  // — Theme —
  sun: 'M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41',
  moon: 'M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z',
  monitor: 'M8 21h8M12 17v4',

  // — Formatting —
  bold: 'M6 12h9a4 4 0 0 1 0 8H7a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h7a4 4 0 0 1 0 8',
  italic: 'M19 4h-9M14 20H5M15 4 9 20',
  strikethrough: 'M16 4H9a3 3 0 0 0-2.83 4M14 12a4 4 0 0 1 0 8H6M4 12h16',
  code: 'm16 18 6-6-6-6M8 6l-6 6 6 6',
  braces:
    'M8 3H7a2 2 0 0 0-2 2v5a2 2 0 0 1-2 2 2 2 0 0 1 2 2v5c0 1.1.9 2 2 2h1M16 21h1a2 2 0 0 0 2-2v-5c0-1.1.9-2 2-2a2 2 0 0 1-2-2V5a2 2 0 0 0-2-2h-1',
  quote: 'M17 6H3M21 12H8M21 18H8M3 12v6',
  calendar: 'M8 2v4M16 2v4M3 10h18',
  table: 'M12 3v18M3 9h18M3 15h18',
  'arrow-up': 'm5 12 7-7 7 7M12 19V5',
  'arrow-down': 'M12 5v14M19 12l-7 7-7-7',
  list: 'M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01',
  'list-ordered': 'M10 6h11M10 12h11M10 18h11M4 6h1v4M4 10h2M6 18H4c0-1 2-2 2-3s-1-1.5-2-1',
  'list-checks': 'm3 7 2 2 4-4M3 17l2 2 4-4M13 6h8M13 12h8M13 18h8',
  'heading-1': 'M4 12h8M4 18V6M12 18V6M17 12l3-2v8',
  'heading-2': 'M4 12h8M4 18V6M12 18V6M21 18h-4c0-4 4-3 4-6 0-1.5-2-2.5-4-1',
  'heading-3':
    'M4 12h8M4 18V6M12 18V6M17.5 10.5c1.7-1 3.5 0 3.5 1.5a2 2 0 0 1-2 2M17 17.5c2 1.5 4 .3 4-1.5a2 2 0 0 0-2-2',
} as const;

/** Extra geometry (circles/rects) for icons a single path can't express. */
const EXTRA_SHAPES: Partial<Record<IconName, JSX.Element>> = {
  search: <circle cx="11" cy="11" r="8" />,
  settings: <circle cx="12" cy="12" r="3" />,
  sun: <circle cx="12" cy="12" r="4" />,
  monitor: <rect width="20" height="14" x="2" y="3" rx="2" />,
  lock: <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />,
  copy: <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />,
  tag: <circle cx="7.5" cy="7.5" r="1.2" />,
  calendar: <rect width="18" height="18" x="3" y="4" rx="2" />,
  table: <rect width="18" height="18" x="3" y="3" rx="2" />,
};

/** Icons drawn as solid shapes rather than strokes. */
const FILL_PATHS = {
  'more-horizontal':
    'M12 10.5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3M19 10.5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3M5 10.5a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3',
  dot: 'M12 7a5 5 0 1 0 0 10 5 5 0 0 0 0-10',
} as const;

/** The star reads as a state toggle, so it needs an outline and a solid twin. */
const STAR =
  'M12 2.5l2.95 5.98 6.6.96-4.78 4.65 1.13 6.57L12 17.56l-5.9 3.1 1.13-6.57L2.45 9.44l6.6-.96z';

/** Tag needs a stroked body plus the punched hole from `EXTRA_SHAPES`. */
const TAG =
  'M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z';

export type IconName =
  keyof typeof STROKE_PATHS | keyof typeof FILL_PATHS | 'star' | 'star-filled' | 'tag';

export interface IconProps {
  name: IconName;
  /** Edge length in px. Defaults to 16, the size the toolbar/sidebar buttons use. */
  size?: number;
  className?: string;
  /**
   * Accessible name for an icon that stands alone. Omit inside a labelled
   * button — the icon is decorative there and stays `aria-hidden`.
   */
  title?: string;
}

export function Icon({ name, size = 16, className, title }: IconProps) {
  const filled = name === 'star-filled' || name in FILL_PATHS;
  const d =
    name === 'star' || name === 'star-filled'
      ? STAR
      : name === 'tag'
        ? TAG
        : filled
          ? FILL_PATHS[name as keyof typeof FILL_PATHS]
          : STROKE_PATHS[name as keyof typeof STROKE_PATHS];
  // `star-filled` is the solid twin of a stroked shape, so it keeps the stroke
  // (a bare fill would read a hair smaller than its outline sibling).
  const solid = name in FILL_PATHS;

  return (
    <svg
      className={className ? `mb-icon ${className}` : 'mb-icon'}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={solid || name === 'star-filled' ? 'currentColor' : 'none'}
      stroke={solid ? 'none' : 'currentColor'}
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
      aria-label={title}
      focusable="false"
    >
      {title && <title>{title}</title>}
      <path d={d} />
      {EXTRA_SHAPES[name]}
    </svg>
  );
}
